// M004-F0749 (WebUI #191). Pure model for the Invoices tab, coded to Spec core-ledger-account-gateway 1.14.0 (InvoiceSummary, InvoiceDetail,
// InvoiceDelivery). No React here, so each rule is unit-tested directly. Nothing is shown that the Ledger did not send.

// The permissions the Spec's invoice ops declare: billing_transactions_view OR ledger_manage (never anything else). Fail closed.
export const canViewInvoices = (permissions) => permissions?.billing_transactions_view === true || permissions?.ledger_manage === true;

export const UNSUPPORTED_CODE = 'LEDGER_ACCOUNT_OPERATION_UNSUPPORTED';
// The Ledger is not reachable: no HTTP answer at all, a gateway-class status, or one of Core's own "node is away" codes.
const UNREACHABLE_CODES = new Set(['LEDGER_NODE_OFFLINE', 'TUNNEL_NODE_TIMEOUT', 'LEDGER_ACCOUNT_UNAVAILABLE', 'LEDGER_ACCOUNT_RESPONSE_INVALID', 'BAD_GATEWAY']);

export function errorInfo(cause) {
  const body = cause?.response?.data;
  const code = (typeof body?.error === 'string' ? body.error : body?.error?.code) || body?.code || '';
  const message = body?.error?.message || body?.message || cause?.message || 'The invoices could not be loaded.';
  const status = cause?.response?.status;
  return {
    code,
    message,
    status,
    unsupported: code === UNSUPPORTED_CODE,
    unreachable: !cause?.response || [502, 503, 504].includes(status) || UNREACHABLE_CODES.has(code),
  };
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})/;
// Calendar days are formatted by parts, so no timezone shifts them.
export const formatDay = (value) => {
  const match = DAY.exec(String(value || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
};

export const money = (minor, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(minor || 0) / 100);

// Delivery (ACCT-INV-003) is shown as the Ledger states it: never "Emailed" without a delivered state.
export function deliveryText(delivery) {
  switch (delivery?.status) {
    case 'delivered': return delivery.delivered_at ? `Emailed ${formatDay(delivery.delivered_at)}` : 'Emailed';
    case 'queued': return 'Email queued, not sent yet';
    case 'failed': return 'Email failed';
    case 'not_configured': return 'Not emailed';
    default: return 'Delivery unknown';
  }
}

export const STATUS_TEXT = Object.freeze({ issued: 'Issued' });
export const statusText = (status) => STATUS_TEXT[status] || String(status || 'Unknown');

// Newest issue date first; the same day orders by number, newest first.
export const sortNewest = (rows) => [...rows].sort((a, b) => {
  if (a.issue_date !== b.issue_date) return a.issue_date < b.issue_date ? 1 : -1;
  if (a.number === b.number) return 0;
  return a.number < b.number ? 1 : -1;
});

export const monthOf = (row) => String(row?.issue_date || '').slice(0, 7);
export const monthOptions = (rows) => [...new Set(rows.map(monthOf).filter(Boolean))].sort().reverse();
export const monthLabel = (month) => {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

// Customer and number are case-insensitive substrings; month is exact. Empty filters keep everything.
export function filterInvoices(rows, { customer = '', number = '', month = '' } = {}) {
  const c = customer.trim().toLowerCase();
  const n = number.trim().toLowerCase();
  return rows.filter((row) => (!c || String(row.customer_name).toLowerCase().includes(c))
    && (!n || String(row.number).toLowerCase().includes(n))
    && (!month || monthOf(row) === month));
}

export const hasFilters = ({ customer = '', number = '', month = '' } = {}) => Boolean(customer.trim() || number.trim() || month);

// In-session copy of the last good list per organisation, for the Ledger-down state. Memory only: a reload or sign-out drops it.
const cache = new Map();
export const cacheGet = (orgId) => cache.get(String(orgId)) || null;
export const cachePut = (orgId, rows) => cache.set(String(orgId), { rows, at: new Date().toISOString() });
export const cacheClear = () => cache.clear();
